export async function apiFetch(path, { method = 'GET', body, csrfToken } = {}, retryOnCsrf = true) {
  const res = await fetch(path, {
    method,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  })

  if (res.status === 204) return null

  const payload = await res.json().catch(() => null)

  if (!res.ok) {
    if (shouldRefreshCsrf({ method, payload, retryOnCsrf, status: res.status })) {
      const freshToken = await fetchCsrfToken()
      return apiFetch(path, { method, body, csrfToken: freshToken }, false)
    }

    const message = payload?.errors?.[0] ?? payload?.error ?? `HTTP ${res.status}`
    const err = new Error(message)
    err.status = res.status
    err.payload = payload
    throw err
  }

  return payload
}

export async function apiFormFetch(path, { method = 'POST', formData, csrfToken } = {}, retryOnCsrf = true) {
  const res = await fetch(path, {
    method,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
    },
    body: formData,
  })

  if (res.status === 204) return null

  const payload = await res.json().catch(() => null)

  if (!res.ok) {
    if (shouldRefreshCsrf({ method, payload, retryOnCsrf, status: res.status })) {
      const freshToken = await fetchCsrfToken()
      return apiFormFetch(path, { method, formData, csrfToken: freshToken }, false)
    }

    const message = payload?.errors?.[0] ?? payload?.error ?? `HTTP ${res.status}`
    const err = new Error(message)
    err.status = res.status
    err.payload = payload
    throw err
  }

  return payload
}

function shouldRefreshCsrf({ method, payload, retryOnCsrf, status }) {
  return retryOnCsrf && method !== 'GET' && status === 422 && !payload
}

async function fetchCsrfToken() {
  const res = await fetch('/users/csrf', {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  })
  const payload = await res.json().catch(() => null)
  if (!res.ok || !payload?.csrf_token) throw new Error('Could not refresh CSRF token')

  return payload.csrf_token
}

export function getActivationProfile({ csrfToken } = {}) {
  return apiFetch('/api/v1/activation/profile', { csrfToken })
}

export function saveActivationStep(step, data, { csrfToken } = {}) {
  return apiFetch(`/api/v1/activation/profile/steps/${step}`, {
    method: 'PATCH',
    body: { activation_step: data },
    csrfToken,
  })
}

export function sendStepOneAgentMessage(data, { csrfToken } = {}) {
  return apiFetch('/api/v1/activation/step-one-agent/messages', {
    method: 'POST',
    body: data,
    csrfToken,
  })
}

export function sendStepTwoAgentMessage(data, { csrfToken } = {}) {
  return apiFetch('/api/v1/activation/step-two-agent/messages', {
    method: 'POST',
    body: data,
    csrfToken,
  })
}

export function sendStepFiveAgentMessage(data, { csrfToken } = {}) {
  return apiFetch('/api/v1/activation/step-five-agent/messages', {
    method: 'POST',
    body: data,
    csrfToken,
  })
}

export function generateActivationAreaSuggestions(data, { csrfToken } = {}) {
  return apiFetch('/api/v1/activation/area-suggestions', {
    method: 'POST',
    body: { area_suggestion: data },
    csrfToken,
  })
}

export function generateStepThreeAgentRecommendations(data, { csrfToken } = {}) {
  return apiFetch('/api/v1/activation/step-three-agent/recommendations', {
    method: 'POST',
    body: { stepThreeAgentRecommendation: data },
    csrfToken,
  })
}

export function generateStepThreeMarketAnalyses(data, { csrfToken } = {}) {
  return apiFetch('/api/v1/activation/step-three-agent/market-analyses', {
    method: 'POST',
    body: { stepThreeMarketAnalysis: data },
    csrfToken,
  })
}

export function scanActivationMarkets({ csrfToken, force = false } = {}) {
  return apiFetch('/api/v1/activation/market-scan-runs', {
    method: 'POST',
    body: { force },
    csrfToken,
  })
}

export function getActivationMarketScanRun(runId, { csrfToken } = {}) {
  return apiFetch(`/api/v1/activation/market-scan-runs/${runId}`, { csrfToken })
}

export function getDeals({ csrfToken } = {}) {
  return apiFetch('/api/v1/deals', { csrfToken })
}

export function createDeal(data, { csrfToken } = {}) {
  return apiFetch('/api/v1/deals', {
    method: 'POST',
    body: { deal: data },
    csrfToken,
  })
}

export function updateDealApi(id, data, { csrfToken } = {}) {
  return apiFetch(`/api/v1/deals/${id}`, {
    method: 'PATCH',
    body: { deal: data },
    csrfToken,
  })
}

export function deleteDealApi(id, { confirmation, csrfToken } = {}) {
  return apiFetch(`/api/v1/deals/${id}`, {
    method: 'DELETE',
    body: { confirmation },
    csrfToken,
  })
}

export function completeDealPriorityAction(id, { csrfToken } = {}) {
  return apiFetch(`/api/v1/deal-priority-actions/${id}/complete`, {
    method: 'POST',
    csrfToken,
  })
}

export function dismissDealPriorityAction(id, { csrfToken } = {}) {
  return apiFetch(`/api/v1/deal-priority-actions/${id}/dismiss`, {
    method: 'POST',
    csrfToken,
  })
}

export function snoozeDealPriorityAction(id, { csrfToken, snoozeUntil } = {}) {
  return apiFetch(`/api/v1/deal-priority-actions/${id}/snooze`, {
    method: 'POST',
    body: { snoozeUntil },
    csrfToken,
  })
}

export function getDealEnrichment(dealId, { csrfToken } = {}) {
  return apiFetch(`/api/v1/deals/${dealId}/enrichment`, { csrfToken })
}

export function getDealComparables(dealId, { csrfToken } = {}) {
  return apiFetch(`/api/v1/deals/${dealId}/comparables`, { csrfToken })
}

export function updateDealEnrichment(dealId, data, { csrfToken } = {}) {
  return apiFetch(`/api/v1/deals/${dealId}/enrichment`, {
    method: 'PATCH',
    body: { deal_enrichment: data },
    csrfToken,
  })
}

export function createDealUnit(dealId, data, { csrfToken } = {}) {
  return apiFetch(`/api/v1/deals/${dealId}/units`, {
    method: 'POST',
    body: { deal_unit: data },
    csrfToken,
  })
}

export function updateDealUnit(dealId, unitId, data, { csrfToken } = {}) {
  return apiFetch(`/api/v1/deals/${dealId}/units/${unitId}`, {
    method: 'PATCH',
    body: { deal_unit: data },
    csrfToken,
  })
}

export function archiveDealUnit(dealId, unitId, { csrfToken } = {}) {
  return apiFetch(`/api/v1/deals/${dealId}/units/${unitId}`, {
    method: 'DELETE',
    csrfToken,
  })
}

export function uploadDealFile(dealId, data, { csrfToken } = {}) {
  const formData = new FormData()
  Object.entries(data).forEach(([key, value]) => {
    if (value !== null && value !== undefined) formData.append(`deal_file[${key}]`, value)
  })

  return apiFormFetch(`/api/v1/deals/${dealId}/files`, {
    method: 'POST',
    formData,
    csrfToken,
  })
}

export function archiveDealFile(dealId, fileId, { csrfToken } = {}) {
  return apiFetch(`/api/v1/deals/${dealId}/files/${fileId}`, {
    method: 'DELETE',
    csrfToken,
  })
}

export function setDealCoverFile(dealId, fileId, { csrfToken } = {}) {
  return apiFetch(`/api/v1/deals/${dealId}/files/${fileId}/cover`, {
    method: 'PATCH',
    body: { deal_file: { activitySource: 'manual' } },
    csrfToken,
  })
}

export function getDealVendor(dealId, { csrfToken } = {}) {
  return apiFetch(`/api/v1/deals/${dealId}/vendor`, { csrfToken })
}

export function updateDealVendor(dealId, data, { csrfToken } = {}) {
  return apiFetch(`/api/v1/deals/${dealId}/vendor`, {
    method: 'PATCH',
    body: { deal_vendor: data },
    csrfToken,
  })
}

export function createDealNote(dealId, data, { csrfToken } = {}) {
  return apiFetch(`/api/v1/deals/${dealId}/notes`, {
    method: 'POST',
    body: { deal_note: data },
    csrfToken,
  })
}

export function getDealNotes(dealId, { csrfToken } = {}) {
  return apiFetch(`/api/v1/deals/${dealId}/notes`, { csrfToken })
}

export function createDealVoiceNote(dealId, data, { csrfToken } = {}) {
  const formData = new FormData()
  Object.entries(data).forEach(([key, value]) => {
    if (value === null || value === undefined) return

    if (key === 'metadata' && typeof value === 'object' && !(value instanceof File)) {
      Object.entries(value).forEach(([metadataKey, metadataValue]) => {
        formData.append(`deal_note[metadata][${metadataKey}]`, metadataValue)
      })
      return
    }

    formData.append(`deal_note[${key}]`, value)
  })

  return apiFormFetch(`/api/v1/deals/${dealId}/notes`, {
    method: 'POST',
    formData,
    csrfToken,
  })
}

export function updateDealNote(dealId, noteId, data, { csrfToken } = {}) {
  return apiFetch(`/api/v1/deals/${dealId}/notes/${noteId}`, {
    method: 'PATCH',
    body: { deal_note: data },
    csrfToken,
  })
}

export function archiveDealNote(dealId, noteId, { csrfToken } = {}) {
  return apiFetch(`/api/v1/deals/${dealId}/notes/${noteId}`, {
    method: 'DELETE',
    csrfToken,
  })
}

export function getDealConcept(dealId, { csrfToken } = {}) {
  return apiFetch(`/api/v1/deals/${dealId}/concept`, { csrfToken })
}

export function generateDealConceptStrategies(dealId, data = {}, { csrfToken } = {}) {
  return apiFetch(`/api/v1/deals/${dealId}/concept/strategies`, {
    method: 'POST',
    body: { deal_concept: data },
    csrfToken,
  })
}

export function generateDealConceptInvestmentThesis(dealId, data = {}, { csrfToken } = {}) {
  return apiFetch(`/api/v1/deals/${dealId}/concept/investment_thesis`, {
    method: 'POST',
    body: { deal_concept: data },
    csrfToken,
  })
}

export function generateDealConceptCommercialisationStrategies(dealId, data = {}, { csrfToken } = {}) {
  return apiFetch(`/api/v1/deals/${dealId}/concept/commercialisation_strategies`, {
    method: 'POST',
    body: { deal_concept: data },
    csrfToken,
  })
}

export function getDealConceptGeneration(dealId, generationId, { csrfToken } = {}) {
  return apiFetch(`/api/v1/deals/${dealId}/concept/generations/${generationId}`, { csrfToken })
}

export function selectDealConceptStrategy(dealId, strategyId, { csrfToken } = {}) {
  return apiFetch(`/api/v1/deals/${dealId}/concept/strategies/${strategyId}/select`, {
    method: 'PATCH',
    csrfToken,
  })
}

export function generateDealConceptValueEnhancements(dealId, strategyId, { csrfToken } = {}) {
  return apiFetch(`/api/v1/deals/${dealId}/concept/strategies/${strategyId}/value_enhancements`, {
    method: 'POST',
    csrfToken,
  })
}

export function createDealConceptFeedback(dealId, strategyId, data, { csrfToken } = {}) {
  return apiFetch(`/api/v1/deals/${dealId}/concept/strategies/${strategyId}/feedback`, {
    method: 'POST',
    body: { deal_concept_feedback: data },
    csrfToken,
  })
}

export function getDealAppraisal(dealId, { csrfToken } = {}) {
  return apiFetch(`/api/v1/deals/${dealId}/appraisal`, { csrfToken })
}

export function updateDealAppraisal(dealId, data, { csrfToken } = {}) {
  return apiFetch(`/api/v1/deals/${dealId}/appraisal`, {
    method: 'PATCH',
    body: { deal_appraisal: data },
    csrfToken,
  })
}

export function createDealAppraisalSnapshot(dealId, data, { csrfToken } = {}) {
  return apiFetch(`/api/v1/deals/${dealId}/appraisal/snapshots`, {
    method: 'POST',
    body: { deal_appraisal_snapshot: data },
    csrfToken,
  })
}

export function getDealNegotiationGuidance(dealId, { csrfToken } = {}) {
  return apiFetch(`/api/v1/deals/${dealId}/negotiation-guidance`, { csrfToken })
}

export function generateDealNegotiationGuidance(dealId, { csrfToken } = {}) {
  return apiFetch(`/api/v1/deals/${dealId}/negotiation-guidance`, {
    method: 'POST',
    csrfToken,
  })
}

export function getDealNegotiationGuidanceGeneration(dealId, guidanceId, { csrfToken } = {}) {
  return apiFetch(`/api/v1/deals/${dealId}/negotiation-guidance/${guidanceId}`, { csrfToken })
}

export function selectDealAppraisalSnapshot(dealId, snapshotId, { csrfToken } = {}) {
  return apiFetch(`/api/v1/deals/${dealId}/appraisal/snapshots/${snapshotId}/select`, {
    method: 'PATCH',
    csrfToken,
  })
}

export function getDealOffers(dealId, { csrfToken } = {}) {
  return apiFetch(`/api/v1/deals/${dealId}/offers`, { csrfToken })
}

export function createDealOffer(dealId, data, { csrfToken } = {}) {
  return apiFetch(`/api/v1/deals/${dealId}/offers`, {
    method: 'POST',
    body: { deal_offer: data },
    csrfToken,
  })
}

export function updateDealOffer(dealId, offerId, data, { csrfToken } = {}) {
  return apiFetch(`/api/v1/deals/${dealId}/offers/${offerId}`, {
    method: 'PATCH',
    body: { deal_offer: data },
    csrfToken,
  })
}

export function archiveDealOffer(dealId, offerId, { csrfToken } = {}) {
  return apiFetch(`/api/v1/deals/${dealId}/offers/${offerId}`, {
    method: 'DELETE',
    csrfToken,
  })
}

export function getDealRiskFlags(dealId, { csrfToken } = {}) {
  return apiFetch(`/api/v1/deals/${dealId}/risk-flags`, { csrfToken })
}

export function updateDealRiskFlag(dealId, flagId, data, { csrfToken } = {}) {
  return apiFetch(`/api/v1/deals/${dealId}/risk-flags/${flagId}`, {
    method: 'PATCH',
    body: { deal_risk_flag: data },
    csrfToken,
  })
}

export function getDealAskRob(dealId, { csrfToken } = {}) {
  return apiFetch(`/api/v1/deals/${dealId}/ask-rob`, { csrfToken })
}

export function sendDealAskRobMessage(dealId, data, { csrfToken } = {}) {
  return apiFetch(`/api/v1/deals/${dealId}/ask-rob/messages`, {
    method: 'POST',
    body: { deal_agent_message: data },
    csrfToken,
  })
}

export function acceptDealAskRobSuggestion(dealId, suggestionId, { csrfToken } = {}) {
  return apiFetch(`/api/v1/deals/${dealId}/ask-rob/suggestions/${suggestionId}/accept`, {
    method: 'POST',
    csrfToken,
  })
}

export function rejectDealAskRobSuggestion(dealId, suggestionId, data = {}, { csrfToken } = {}) {
  return apiFetch(`/api/v1/deals/${dealId}/ask-rob/suggestions/${suggestionId}/reject`, {
    method: 'POST',
    body: data,
    csrfToken,
  })
}
